<script lang="ts">
  /**
   * Legal and privacy.
   *
   * The last screen missing from the design set, and the one place in the app
   * where being *accurate* matters more than being brief. Every claim below was
   * checked against the code rather than written from a template:
   *
   *   - storage keys: lib/device.ts, lib/record.ts, lib/room.svelte.ts,
   *     lib/coefficients.ts, packages/analytics/src/beacon.ts
   *   - what is transmitted: packages/analytics/src/events.ts (the full taxonomy)
   *   - what the server keeps and for how long: apps/api/src/room.ts
   *
   * If any of those change, this has to change with them. A privacy policy that
   * has drifted from the code is worse than none — it is a false statement
   * rather than a missing one.
   */
  interface Props {
    onclose: () => void;
  }
  let { onclose }: Props = $props();

  let tab = $state<'privacy' | 'legal'>('privacy');
</script>

<div class="sheet open" aria-label="Legal and privacy">
  <div class="scrim" onclick={onclose} role="presentation"></div>
  <div class="panel" role="dialog" aria-modal="true" aria-labelledby="legal-title">
    <div class="handle"></div>

    <div class="formats" role="tablist" aria-label="Section" style="margin-bottom:var(--space-4)">
      <button
        class={tab === 'privacy' ? 'on' : ''}
        role="tab"
        aria-selected={tab === 'privacy'}
        onclick={() => (tab = 'privacy')}>Privacy</button
      >
      <button
        class={tab === 'legal' ? 'on' : ''}
        role="tab"
        aria-selected={tab === 'legal'}
        onclick={() => (tab = 'legal')}>Legal</button
      >
    </div>

    {#if tab === 'privacy'}
      <h2 id="legal-title">Privacy</h2>

      <p class="muted">
        There is no account, no email and no password. You are a random string this device made up,
        and nothing here is ever linked to you as a person.
      </p>

      <p class="eyebrow" style="margin-top:var(--space-4)">What we never collect</p>
      <div class="card">
        <p style="margin:0;font-size:var(--text-sm)">
          Your name, email, phone number or address. Your IP address is never stored — the server
          sees it to answer the request, as every web server must, and keeps only the two-letter
          country code the network edge reports.
        </p>
      </div>

      <p class="eyebrow" style="margin-top:var(--space-4)">What stays on this device</p>
      <div class="card">
        <p style="margin:0 0 var(--space-2);font-size:var(--text-sm)">
          Never sent anywhere, and cleared if you clear site data:
        </p>
        <p class="faint" style="margin:0">
          A random device ID · your played/won/best record · the code of a 1v1 room you are in ·
          a cached copy of the simulation settings so the game works offline · the app itself,
          cached so it opens without a connection.
        </p>
      </div>

      <p class="eyebrow" style="margin-top:var(--space-4)">What is sent, and why</p>
      <div class="card">
        <p style="margin:0 0 var(--space-2);font-size:var(--text-sm)">
          Anonymous usage events, so we can see where the game is confusing and fix it. Things like
          "a draft was abandoned at pick 7" or "a match finished".
        </p>
        <p class="faint" style="margin:0">
          Each carries the random device ID, a session ID, the app version, whether you are on a
          phone or desktop, a rough connection type, and your country. No third-party analytics
          service is involved — no Google Analytics, no advertising trackers, no cookies for
          tracking. The data goes to our own server and stays there.
        </p>
      </div>

      <p class="eyebrow" style="margin-top:var(--space-4)">1 vs 1 rooms</p>
      <div class="card">
        <p style="margin:0;font-size:var(--text-sm)">
          A room holds both players' device IDs, the team names you typed, the squads you drafted
          and the choices you made during the match. Your opponent sees your team name, your squad
          and your score — nothing else. Rooms are deleted seven days after they expire.
        </p>
      </div>

      <p class="eyebrow" style="margin-top:var(--space-4)">Deleting your data</p>
      <div class="card">
        <p style="margin:0;font-size:var(--text-sm)">
          Clearing this site's data in your browser removes everything stored on the device and
          gives you a new random ID, which disconnects you from any past events. Room data deletes
          itself on the schedule above. Because nothing identifies you, we cannot look up "your"
          data on request — there is nothing to look it up by.
        </p>
      </div>
    {:else}
      <h2 id="legal-title">Legal</h2>

      <p class="eyebrow" style="margin-top:var(--space-4)">Not affiliated with anyone</p>
      <div class="card">
        <p style="margin:0;font-size:var(--text-sm)">
          pickthe11 is an unofficial, fan-made game. It is not affiliated with, endorsed by or
          connected to any cricket board, league, franchise, broadcaster or player.
        </p>
      </div>

      <p class="eyebrow" style="margin-top:var(--space-4)">Names and data</p>
      <div class="card">
        <p style="margin:0;font-size:var(--text-sm)">
          Player names and match statistics come from publicly available ball-by-ball records of
          professional matches. Teams are shown as neutral city labels — "Chennai 2016" — and no
          club names, crests, kits or logos are used. Ratings are our own calculations from that
          public data.
        </p>
      </div>

      <p class="eyebrow" style="margin-top:var(--space-4)">The game is a simulation</p>
      <div class="card">
        <p style="margin:0;font-size:var(--text-sm)">
          Results are generated by a computer model, not predictions of real matches. There is no
          wagering, no prize and no real-money element of any kind.
        </p>
      </div>

      <p class="eyebrow" style="margin-top:var(--space-4)">No warranty</p>
      <div class="card">
        <p style="margin:0;font-size:var(--text-sm)">
          Provided as-is, with no guarantee that it works, stays available, or keeps your data.
          It is a game.
        </p>
      </div>

      <p class="faint" style="margin-top:var(--space-4)">
        Questions: support@pickthe11.com
      </p>
    {/if}

    <button class="btn-secondary" style="width:100%;margin-top:var(--space-4)" onclick={onclose}>
      Close
    </button>
  </div>
</div>
